import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Avatar,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { LuHammer, LuPalette } from "react-icons/lu";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { renderCurrencyIcon } from "../../../utils/CurrencyUtils";
import moment from "moment";
import { statusStyleArray } from "../config";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
  marginBottom: "16px",
  cursor: "pointer",
  overflow: "hidden",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    boxShadow: "0px 6px 20px rgba(0, 0, 0, 0.15)",
  },
}));

// Pills overlaid on the active-request card hero — mirrors the mobile
// card so the project_type / location info is visible on desktop too.
const ProjectTypePill = styled(Box)({
  position: "absolute",
  top: "10px",
  left: "10px",
  padding: "4px 10px",
  borderRadius: "999px",
  backgroundColor: "#FFFFFF",
  color: "#0D0D0E",
  fontSize: "12px",
  fontWeight: 600,
  fontFamily: "Inter Tight",
  textTransform: "capitalize",
  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  letterSpacing: 0,
});

// Scope type badge — icon + gradient so design and execution cards read
// differently at a glance. Sits top-right on the card hero.
export const ScopeTypeBadge = ({ item }) => {
  const design = isDesignScope(item);
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        padding: "4px 10px",
        borderRadius: "999px",
        background: design
          ? "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)"
          : "linear-gradient(135deg, #0EA5E9 0%, #4D6BF6 100%)",
        color: "#FFFFFF",
        fontSize: "12px",
        fontWeight: 600,
        fontFamily: "Inter Tight",
        letterSpacing: 0,
        whiteSpace: "nowrap",
        boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
        flexShrink: 0,
      }}
    >
      {design ? <LuPalette size={13} /> : <LuHammer size={13} />}
      {design ? "Design" : "Execution"}
    </Box>
  );
};

const StatusChip = styled(Chip)(({ theme, status }) => ({
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "600",
  height: "24px",
  backgroundColor: status === "urgent" ? "#FF4444" : "#10B981",
  color: "#FFFFFF",
}));

const PriceRange = styled(Typography)(({ theme }) => ({
  color: "#FFFFFF",
  fontSize: "12xpx",
  fontWeight: "600",
  position: "absolute",
  bottom: "6px",
  right: "12px",
  backgroundColor: "#00000080",
  padding: "4px 6px",
  borderRadius: "12px",
}));

const ProjectName = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: "600",
  color: "#0D0D0E",
  marginBottom: "0px",
}));

const ProjectSummary = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: "#949DAA",
  marginBottom: "8px",
}));

const OverdueText = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  color: "#FF4444",
  fontWeight: "500",
  marginBottom: "12px",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#4D6BF6",
  color: "#FFFFFF",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: "600",
  textTransform: "none",
  width: "100%",
  padding: "8px 16px",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#3B5BDB",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  fontWeight: "600",
  color: "#0D0D0E",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const CountBadge = styled(Box)(({ theme }) => ({
  backgroundColor: "#E5E7EB",
  color: "#6B7280",
  borderRadius: "50%",
  width: "24px",
  height: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: "600",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
}));

// Scope type helpers — design scopes carry a non-null `scope_json`
// (deliverables/payments) on the bid list item; execution scopes have
// scope_json = null.
export const isDesignScope = (item) => item?.scope_json != null;

export const getScopeTypeLabel = (item) =>
  isDesignScope(item) ? "Design" : "Execution";

// Amount shown in the list. Design bids (proposal_type === "DESIGN") use
// project_cost; everything else keeps the existing field mapping.
export const getBidAmount = (item) => {
  if (item?.proposal_type === "DESIGN") {
    return item?.project_cost ?? item?.proposal?.project_cost ?? null;
  }
  return item?.proposal?.amount || item?.proposal_cost || item?.amount || null;
};

export default function RFQDetails({
  mode = "active",
  // Active Requests
  activeRequests = [],
  activeLoading = false,
  activePagination,
  activePage = 1,
  onActivePageChange,
  // Submitted (status=proposal)
  submitted = [],
  submittedLoading = false,
  submittedPagination,
  submittedPage = 1,
  onSubmittedPageChange,
  // Submitted (status=awaiting-project-pricing)
  pricing = [],
  pricingLoading = false,
  pricingPagination,
  pricingPage = 1,
  onPricingPageChange,
  // Awarded (status=approved-by-contractor)
  awarded = [],
  awardedLoading = false,
  awardedPagination,
  awardedPage = 1,
  onAwardedPageChange,
  // Archived
  archived = [],
  archivedLoading = false,
  archivedPagination,
  archivedPage = 1,
  onArchivedPageChange,
}) {
  const navigate = useNavigate();

  const getStatusDisplayText = (status) => {
    if (!status) return "-";
    return status
      .replace(/[-_]/g, " ")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Design scopes have their own bid flow (deliverables/payments) —
  // route them to the design pricing page instead of the BOQ one.
  const getPricingPath = (item) =>
    isDesignScope(item)
      ? `/rfq/design-pricing/${item.scope_id}/${item.project_id}`
      : `/rfq/pricing/${item.scope_id}/${item.project_id}`;

  const handleRowClick = (item) => {
    navigate(getPricingPath(item));
  };

  const handleAddPricing = (item) => {
    navigate(getPricingPath(item));
  };

  const renderActiveRequestCard = (item) => (
    <StyledCard key={item.id} onClick={() => handleAddPricing(item)}>
      <Box sx={{ position: "relative" }}>
        <CardMedia
          component="img"
          height="140"
          image={item.project_image?.[0]?.image || "/api/placeholder/300/140"}
          alt={item.name}
          sx={{
            backgroundColor: "#F2F4F7",
            objectFit: "cover",
          }}
        />
        {/* Project type (left) + scope type (right) on one row */}
        <Box
          sx={{
            position: "absolute",
            top: "10px",
            left: "10px",
            right: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {item.project_type ? (
            <ProjectTypePill sx={{ position: "static" }}>
              {item.project_type}
            </ProjectTypePill>
          ) : (
            <span />
          )}
          <ScopeTypeBadge item={item} />
        </Box>
      </Box>

      <CardContent sx={{ padding: "16px" }}>
        <Box
          sx={{ display: "flex", alignItems: "flex-start", marginBottom: "6px" }}
        >
          <Avatar
            sx={{
              width: "30px",
              height: "30px",
              marginRight: "12px",
              backgroundColor: "#E5E7EB",
              fontSize: "14px",
              marginTop: "4px",
            }}
          >
            {item.username?.charAt(0) || "M"}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <ProjectName noWrap>{item.name}</ProjectName>
            <ProjectSummary noWrap>
              {item.username || item.user_data?.username || "-"}
            </ProjectSummary>
          </Box>
        </Box>



        <ActionButton
          onClick={(e) => {
            e.stopPropagation();
            handleAddPricing(item);
          }}
        >
          Add Pricing
        </ActionButton>
      </CardContent>
    </StyledCard>
  );

  return (
    <Box
      sx={{
        padding: "0 0.5vw",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {mode === "active" && (
        <ActiveRequestsSection
          items={activeRequests}
          loading={activeLoading}
          pagination={activePagination}
          page={activePage}
          onPageChange={onActivePageChange}
          renderCard={renderActiveRequestCard}
        />
      )}

      {mode === "submitted" && (
        <BidTableSection
          title="Submitted"
          items={submitted}
          loading={submittedLoading}
          pagination={submittedPagination}
          page={submittedPage}
          onPageChange={onSubmittedPageChange}
          emptyText="No submitted bids yet"
          onRowClick={handleRowClick}
          getStatusDisplayText={getStatusDisplayText}
        />
      )}

      {mode === "pricing" && (
        <BidTableSection
          title="Submitted"
          items={pricing}
          loading={pricingLoading}
          pagination={pricingPagination}
          page={pricingPage}
          onPageChange={onPricingPageChange}
          emptyText="No submitted bids yet"
          onRowClick={handleRowClick}
          getStatusDisplayText={getStatusDisplayText}
        />
      )}

      {mode === "awarded" && (
        <BidTableSection
          title="Awarded"
          items={awarded}
          loading={awardedLoading}
          pagination={awardedPagination}
          page={awardedPage}
          onPageChange={onAwardedPageChange}
          emptyText="No awarded bids yet"
          onRowClick={handleRowClick}
          getStatusDisplayText={getStatusDisplayText}
        />
      )}

      {mode === "archived" && (
        <BidTableSection
          title="Archived"
          items={archived}
          loading={archivedLoading}
          pagination={archivedPagination}
          page={archivedPage}
          onPageChange={onArchivedPageChange}
          emptyText="No archived bids"
          onRowClick={handleRowClick}
          getStatusDisplayText={getStatusDisplayText}
        />
      )}
    </Box>
  );
}

// Active Requests — card grid + its own pagination footer. Empty state
// matches the table sections' style.
function ActiveRequestsSection({
  items,
  loading,
  pagination,
  page,
  onPageChange,
  renderCard,
}) {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (items.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ textAlign: "center", color: "#949DAA", marginTop: 4 }}
      >
        No active requests
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Scrolling lives on this wrapper so the grid rows are sized by
          their card content (image + text + button), not by the
          available vertical space. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          paddingBottom: "8px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "16px",
            alignItems: "start",
          }}
        >
          {items.map(renderCard)}
        </Box>
      </Box>
      {(pagination?.totalPages || 1) > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            background: "#fff",
            padding: "12px 0",
          }}
        >
          <Pagination
            count={pagination?.totalPages || 1}
            page={page}
            onChange={(event, value) => onPageChange(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}

// Renders a section with a table of bids + a sticky pagination footer.
// Used by both Submitted & Awarded and Archived sections; only the
// status set the parent passes to the API differs.
function BidTableSection({
  title,
  items,
  loading,
  pagination,
  page,
  onPageChange,
  emptyText,
  onRowClick,
  getStatusDisplayText,
  wrapperSx,
}) {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        ...(wrapperSx || {}),
      }}
    >
      {/* <SectionTitle>
        {title}
        <CountBadge>{pagination?.totalCount ?? items.length ?? 0}</CountBadge>
      </SectionTitle> */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      ) : items.length > 0 ? (
        <>
          <TableContainer
            component={Paper}
            sx={{
              width: "100%",
              overflow: "none !important",
              boxShadow: "none",
              maxHeight: "77vh",
              overflowY: "scroll",
              scrollbarWidth: "none",
            }}
          >
            <Table
              sx={{
                "& thead": {
                  "& th": {
                    color: "#949DAA",
                    fontWeight: "500",
                  },
                },
                "& tbody": {
                  "& tr:hover": {
                    backgroundColor: "#f7f7f7",
                  },
                  "& tr": {
                    borderBottom: "none",
                  },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Project Name</StyledTableCell>
                  <StyledTableCell>Client</StyledTableCell>
                  <StyledTableCell>Project Type</StyledTableCell>
                  <StyledTableCell>Type</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Amount</StyledTableCell>
                  <StyledTableCell>Start/End Date</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.proposal_id || item.id}
                    onClick={() => onRowClick(item)}
                    sx={{ cursor: "pointer" }}
                  >
                    <StyledTableCell>{item.name || "-"}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          sx={{
                            width: "24px",
                            height: "24px",
                            fontSize: "12px",
                            backgroundColor: "#E5E7EB",
                          }}
                        >
                          {item.username?.charAt(0) || item.user_data?.username?.charAt(0) || "U"}
                        </Avatar>
                        <Typography variant="body2">
                          {item.username || item.user_data?.username || "-"}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>{item.project_type || "-"}</StyledTableCell>
                    <StyledTableCell>
                      <ScopeTypeBadge item={item} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          style={{
                            width: "15px",
                            height: "15px",
                            borderRadius: "50%",
                            backgroundColor:
                              statusStyleArray.find(
                                (style) =>
                                  style.status === (item.status || item.proposal_status)
                              )?.backgroundColor || "transparent",
                            border: "3px solid #e5e5e5",
                          }}
                        />
                        <Typography
                          style={{
                            fontSize: "14px",
                            marginLeft: "8px",
                            color: "#0D0D0E",
                          }}
                        >
                          {getStatusDisplayText(item.status || item.proposal_status)}
                        </Typography>
                      </div>
                    </StyledTableCell>
                    <StyledTableCell>
                      {getBidAmount(item) ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {renderCurrencyIcon("black-small")}
                          {formattedAmountWithoutAed(getBidAmount(item))}
                        </Box>
                      ) : (
                        "-"
                      )}
                    </StyledTableCell>
                    <StyledTableCell>
                      <p>{item.start_date
                        ? moment(item.start_date, "MMMM DD, YYYY").format("MMM DD, YYYY")
                        : "-"}
                        </p>
                        <p>
                        {item.end_date
                        ? moment(item.end_date, "MMMM DD, YYYY").format("MMM DD, YYYY")
                        : "-"}</p>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {(pagination?.totalPages || 1) > 1 && (
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 5,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                background: "#fff",
                padding: "12px 0",
                marginTop: "auto",
              }}
            >
              <Pagination
                count={pagination?.totalPages || 1}
                page={page}
                onChange={(event, value) => onPageChange(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <Typography
          variant="body2"
          sx={{ textAlign: "center", color: "#949DAA", marginTop: 4 }}
        >
          {emptyText}
        </Typography>
      )}
    </Box>
  );
}
